import React, { Suspense, useEffect, useRef, useState, useMemo, useContext, lazy } from "react";
import "./App.css";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  UNSAFE_RouteContext,
  UNSAFE_NavigationContext
} from "react-router-dom";
import { encryptPath, decryptRouteParam } from "./utils/cryptoHelper";

import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import "react-quill/dist/quill.snow.css";
import "./scss/main.scss";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { chats_saver } from "./Pages/actions/chats";
import { MdCallEnd } from "react-icons/md";
import { audio_modal, video_modal } from "./Pages/actions/video_audio_modal";
import AudioCallRingModal from "./components/CommonModals/AudioCallModal";
import VideoCallRingModal from "./components/CommonModals/VideoCallModal";
import socketModel from "./models/socketModel";
import environment from "./environment";
import { SidebarProvider } from "./SidebarContext";

const RouteContextProvider = UNSAFE_RouteContext.Provider;
const NavigationContextProvider = UNSAFE_NavigationContext.Provider;

// Type definitions
interface User {
  id?: string;
  _id?: string;
  role?: string;
  user_type?: string;
  token?: string;
  access_token?: string;
  loggedIn?: boolean;
  plan_id?: string | any;
  addedBy?: {
    plan_id?: string | any;
    role?: string;
    [key: string]: any;
  };
  request_status?: string;
  [key: string]: any;
}

interface LocationPayload {
  reference_id: string;
  lat: number;
  lng: number;
  location: string;
  // accuracy: number;
  // speed: number;
  // bearing: number;
  // altitude: number;
  // timestamp: string;
  savedAt?: number;
  type?:any;
}

interface GeolocationPosition {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    speed: number | null;
    heading: number | null;
    altitude: number | null;
  };
}

interface CallingUserData {
  user_id?: string;
  user_name?: string;
  room_name?: string;
  room_id?: string;
  isGroupChat?: boolean;
  room_image?: string;
  name?: string;
  [key: string]: any;
}

interface RouteConfig {
  url: string;
  path?: string;
  element?: JSX.Element;
}

interface PageRouteProps {
  CallingUserName: { name?: string };
  CallingUserData: CallingUserData;
  children: React.ReactNode;
}

interface RootState {
  user: User;
  chats: {
    data: any[];
    roomids: string[];
  };
  call_modal: {
    video_modal: boolean;
    audio_modal: boolean;
  };
}

// Custom Hook for Driver Location Tracking - Only every 5 minutes
const useDriverLocation = () => {
  const user = useSelector((state: RootState) => state.user);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isTracking, setIsTracking] = useState<boolean>(false);
  const isDriver: boolean = user?.role === 'driver' || user?.user_type === 'driver';

  const getAndSendLocation = (): void => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported by this browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        const { latitude, longitude, accuracy, speed, heading, altitude } = position.coords;

        const payload: LocationPayload = {
          reference_id: user?.id || user?._id || '',
          lat: latitude,
          lng: longitude,
          location: `${latitude},${longitude}`,
          // accuracy: accuracy || 0,
          // speed: speed || 0,
          // bearing: heading || 0,
          // altitude: altitude || 0,
          // timestamp: new Date().toISOString(),
          type:"driver",
        };

        // Emit via socket
        if (socketModel && socketModel.connected) {
          socketModel.emit('tracking-location', payload);
          console.log('Location sent at:', new Date().toLocaleTimeString(), payload);
        } else {
          console.log('Socket not connected, storing location locally');
          const offlineLocations: LocationPayload[] = JSON.parse(localStorage.getItem('offlineLocations') || '[]');
          offlineLocations.push(payload);
          localStorage.setItem('offlineLocations', JSON.stringify(offlineLocations));
        }

        // Store last location in localStorage
        localStorage.setItem('lastDriverLocation', JSON.stringify({
          ...payload,
          savedAt: Date.now()
        }));
      },
      (error: GeolocationPositionError) => {
        console.error('Location error:', error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            console.log('User denied location request');
            break;
          case error.POSITION_UNAVAILABLE:
            console.log('Location information unavailable');
            break;
          case error.TIMEOUT:
            console.log('Location request timed out');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Retry sending offline locations when socket connects
  const retryOfflineLocations = (): void => {
    const offlineLocations: LocationPayload[] = JSON.parse(localStorage.getItem('offlineLocations') || '[]');
    if (offlineLocations.length > 0 && socketModel && socketModel.connected) {
      offlineLocations.forEach(location => {
        socketModel.emit('tracking-location-update', location);
      });
      localStorage.removeItem('offlineLocations');
      console.log('Sent offline locations:', offlineLocations.length);
    }
  };

  // Start tracking - only sets up interval, no immediate call
  const startTracking = (): void => {
    if (!isDriver) {
      console.log('User is not a driver, skipping location tracking');
      return;
    }

    if (!navigator.geolocation) {
      console.log('Geolocation not supported by this browser');
      return;
    }

    // Stop any existing tracking first
    stopTracking();

    // Set up interval to get location every 5 minutes (300000 ms)
    // IMPORTANT: No immediate execution, first call will be after 5 minutes
    intervalRef.current = setInterval(() => {
      console.log('5 minutes passed, getting location...');
      getAndSendLocation();
    }, 300000); 

    setIsTracking(true);
    console.log('Driver location tracking started. Will send location every 5 minutes');
  };

  // Stop tracking
  const stopTracking = (): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
    console.log('Driver location tracking stopped');
  };

  // Send location on demand (manual trigger)
  const sendLocationNow = (): void => {
    console.log('Manual location update triggered');
    getAndSendLocation();
  };

  // Prevent default browser drag and drop navigation (which reloads the app and triggers the loader)
  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent) => {
      if (e.dataTransfer && e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
        e.preventDefault();
      }
    };

    const handleGlobalDrop = (e: DragEvent) => {
      if (e.dataTransfer && e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
        e.preventDefault();
      }
    };

    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('drop', handleGlobalDrop);

    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  // Suppress harmless ResizeObserver loop errors from popping up dev error overlays
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (
        e?.message?.includes('ResizeObserver loop') ||
        e?.message?.includes('undelivered notifications')
      ) {
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  // Listen for socket connection to retry offline locations
  useEffect(() => {
    if (socketModel) {
      socketModel.on('connect', () => {
        console.log('Socket connected, retrying offline locations');
        retryOfflineLocations();
      });
    }

    return () => {
      if (socketModel) {
        socketModel.off('connect');
      }
    };
  }, []);

  // Start tracking when component mounts and user is driver
  useEffect(() => {
    if (user && (user.id || user._id) && isDriver) {
      // Small delay to ensure everything is initialized
      const timeoutId = setTimeout(() => {
        startTracking();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }

    return () => {
      stopTracking();
    };
  }, [user?.id, user?._id, isDriver]);

  return {
    isTracking,
    sendLocationNow,
    stopTracking,
    startTracking
  };
};

const PUBLIC_PATHS = [
  "/",
  "/home",
  "/aboutus",
  "/contactus",
  "/privacypolicy",
  "/termcondtitons",
  "/login",
  "/signup",
  "/signup-step-1",
  "/signup-step-2",
  "/signup-driver-detail",
  "/signup-step-3",
  "/forgotpassword",
  "/otp-verify",
  "/plan",
  "/successpage",
  "/cancelpage",
];

const PLAN_EXEMPT_PATHS = [
  ...PUBLIC_PATHS,
  "/dashboard",
  "/profile",
  "/deleteaccount",
  "/activeplan",
  "/subscription-receipt",
  "/submit-receipt",
  "/chat",
];

const PageRoute: React.FC<PageRouteProps> = ({ CallingUserName, children, CallingUserData }) => {
  const user: User = useSelector((state: RootState) => state.user);
  const history = useNavigate();
  const location = useLocation();
  const CHATS = useSelector((state: RootState) => state.chats?.data);

  const routeContext: any = useContext(UNSAFE_RouteContext);
  const decryptedRouteContext = useMemo(() => {
    if (!routeContext?.matches || routeContext.matches.length === 0) {
      return routeContext;
    }
    const matches = routeContext.matches.map((m: any, idx: number) => {
      if (idx !== routeContext.matches.length - 1 || !m.params) return m;
      const decryptedParams: Record<string, string> = {};
      for (const [k, v] of Object.entries(m.params)) {
        decryptedParams[k] = v ? decryptRouteParam(v as string) : (v as string);
      }
      return { ...m, params: decryptedParams };
    });
    return { ...routeContext, matches };
  }, [routeContext]);

  useEffect(() => {
    const rawPath = location.pathname + location.search + location.hash;
    const encrypted = encryptPath(rawPath);
    if (encrypted !== rawPath) {
      window.history.replaceState(window.history.state, '', encrypted);
    }
  }, [location.pathname, location.search, location.hash]);

  let VoiceCall: any = localStorage.getItem("BackToVoiceCall");
  let VideoCall: any = localStorage.getItem("BackToVedioCall");

  const currentPath = location.pathname;
  const isPublic = PUBLIC_PATHS.some((p) => (p === "/" ? currentPath === "/" : currentPath.startsWith(p)));
  const isPlanExempt = PLAN_EXEMPT_PATHS.some((p) => (p === "/" ? currentPath === "/" : currentPath.startsWith(p)));

  const isRemembered = typeof window !== "undefined" && localStorage.getItem("remember_me") === "true";
  const storedToken = typeof window !== "undefined"
    ? (isRemembered ? localStorage.getItem("token") : null) || sessionStorage.getItem("token") || localStorage.getItem("token")
    : null;
  const isLoggedIn = Boolean(user?.loggedIn || user?.token || user?.access_token || storedToken);
  const isCarrier = user?.role === "carrier" || user?.role === "user";
  const hasPlan = Boolean(
    user?.plan_id ||
    user?.addedBy?.plan_id ||
    user?.addedBy?.role === "admin"
  );

  // F-011: Admin Portal Authorization Boundary Check
  const isAdminPortal =
    typeof window !== "undefined" &&
    (window.location.hostname.startsWith("admin.") ||
      window.location.hostname.includes("admin."));
  const adminRoles = ["admin", "sub_admin", "super_admin"];

  if (isAdminPortal && isLoggedIn && !adminRoles.includes(user?.role || "")) {
    return <Navigate to="/login" replace />;
  }

  // Unauthenticated users attempting to access protected pages are redirected to login
  if (!isLoggedIn && !isPublic) {
    return <Navigate to="/login" replace />;
  }

  // F-008 & F-009: Role-based access control for drivers
  // Restrict drivers from accessing departments, task edit/creation, dispatcher tasks, carriers, loads, staff, etc.
  if (isLoggedIn && user?.role === "driver") {
    if (currentPath === "/dashboard") {
      return <Navigate to="/driver-dashboard" replace />;
    }

    const driverForbiddenPrefixes = [
      "/department",
      "/tasks",
      "/task/edit",
      "/routing",
      "/carriers",
      "/approvedcarriers",
      "/rejectedcarrier",
      "/pendingcarrier",
      "/carrierstaff",
      "/trucks",
      "/drivers",
      "/loads",
      "/pickeduploads",
      "/intransitloads",
      "/deliveredloads",
      "/pendingpickuploads",
      "/team",
      "/delivery-stats",
      "/transaction",
      "/recruitment",
      "/plan",
      "/activeplan",
      "/subscription-receipt",
      "/submit-receipt",
    ];

    const isForbiddenForDriver = driverForbiddenPrefixes.some(
      (prefix) => currentPath === prefix || currentPath.startsWith(prefix + "/")
    );

    if (isForbiddenForDriver) {
      return <Navigate to="/task" replace />;
    }
  }

  // Logged-in carriers without an active plan attempting to access protected operational pages are redirected to plan
  if (isLoggedIn && isCarrier && !hasPlan && !isPlanExempt) {
    return <Navigate to="/plan" replace />;
  }

  const GoOnCall = (): void => {
    if (VoiceCall) {
      VoiceCall = JSON.parse(VoiceCall);
      history(`/chat?room_idd=${VoiceCall?.room_id}&voiceCall=true`);
    }
    if (VideoCall) {
      VideoCall = JSON.parse(VideoCall);
      history(`/chat?room_idd=${VideoCall?.room_id}&videoCall=true`);
    }
  };

  const LocalStorageRemover = (): void => {
    localStorage.removeItem('BackToVedioCall');
    localStorage.removeItem('BackToVoiceCall');
  };

  const CallEndHandler = (): void => {
    let VoiceCall: any = localStorage.getItem("BackToVoiceCall");
    let VideoCall: any = localStorage.getItem("BackToVedioCall");
    const isVideo = Boolean(VideoCall && !VoiceCall);
    const eventToEmit = isVideo ? "stop-video-call" : "stop-voice-call";
    const Data: any = VoiceCall ? JSON.parse(VoiceCall) : (VideoCall ? JSON.parse(VideoCall) : null);
    const roomId = Data?.room_id || CallingUserData?.room_id;

    if (roomId) {
      socketModel.emit(eventToEmit, {
        room_id: roomId,
        user_id: user?.id || user?._id,
      });
    }
    LocalStorageRemover();
    window.location.reload();
  };

  return (
    <RouteContextProvider value={decryptedRouteContext}>
      {(VoiceCall && !window.location.pathname?.includes('/chat')) ?
        <div className="flex justify-between bg-[#cfd3e9] p-[4px_16px]" onClick={GoOnCall}>
          <div></div>
          <div>
            <p className="">{CallingUserName?.name}</p>
          </div>
          <div className="text-[red] bg-[#e5d5d5] p-[1px_5px] rounded-full">
            <MdCallEnd onClick={() => CallEndHandler()} />
          </div>
        </div>
        : <></>}
      {children}
    </RouteContextProvider>
  );
};

interface DriverTrackerWidgetProps {
  isTracking: boolean;
  sendLocationNow: () => void;
  startTracking: () => void;
  stopTracking: () => void;
}

const DriverTrackerWidget: React.FC<DriverTrackerWidgetProps> = ({
  isTracking,
  sendLocationNow,
  startTracking,
  stopTracking,
}) => {
  const [isTrackerMinimized, setIsTrackerMinimized] = useState<boolean>(false);
  const [trackerPosition, setTrackerPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDraggingTracker, setIsDraggingTracker] = useState<boolean>(false);
  const isDraggingTrackerRef = useRef<boolean>(false);
  const trackerDragStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const trackerElementPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const trackerHasMovedRef = useRef<boolean>(false);

  const handleTrackerMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

    isDraggingTrackerRef.current = true;
    setIsDraggingTracker(true);
    trackerHasMovedRef.current = false;
    trackerDragStartPosRef.current = { x: clientX, y: clientY };
    trackerElementPosRef.current = { ...trackerPosition };

    const handleMouseMove = (moveEvent: MouseEvent | TouchEvent) => {
      if (!isDraggingTrackerRef.current) return;
      const moveX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : (moveEvent as MouseEvent).clientX;
      const moveY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : (moveEvent as MouseEvent).clientY;

      const deltaX = moveX - trackerDragStartPosRef.current.x;
      const deltaY = moveY - trackerDragStartPosRef.current.y;

      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        trackerHasMovedRef.current = true;
      }

      setTrackerPosition({
        x: trackerElementPosRef.current.x + deltaX,
        y: trackerElementPosRef.current.y + deltaY
      });
    };

    const handleMouseUp = () => {
      isDraggingTrackerRef.current = false;
      setIsDraggingTracker(false);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleMouseMove);
    window.addEventListener("touchend", handleMouseUp);
  };

  return (
    <div
      className="fixed bottom-4 right-4 bg-green-500 text-white rounded-lg text-xs z-50 shadow-lg select-none cursor-grab active:cursor-grabbing"
      style={{
        transform: `translate(${trackerPosition.x}px, ${trackerPosition.y}px)`,
        transition: isDraggingTracker ? 'none' : 'transform 0.15s ease-out'
      }}
      onMouseDown={handleTrackerMouseDown}
      onTouchStart={handleTrackerMouseDown}
    >
      {isTrackerMinimized ? (
        <div 
          className="p-3 flex items-center justify-center gap-1.5"
          onClick={() => {
            if (trackerHasMovedRef.current) return;
            setIsTrackerMinimized(false);
          }}
          title="Drag to move anywhere | Click to maximize"
        >
          <svg className="w-3.5 h-3.5 opacity-80" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 4a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm6-12a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2z" />
          </svg>
          <div className={`w-3 h-3 rounded-full ${isTracking ? 'bg-green-300 animate-pulse' : 'bg-red-300'}`}></div>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2">
          <div 
            className="flex items-center text-white/80 hover:text-white cursor-grab active:cursor-grabbing pr-1 border-r border-white/20"
            title="Drag to move anywhere"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 4a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm6-12a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2z" />
            </svg>
          </div>
          <div 
            className="flex items-center gap-2 cursor-pointer flex-1"
            onClick={() => {
              if (trackerHasMovedRef.current) return;
              sendLocationNow();
            }}
            title="Click to send location now | Drag to move"
          >
            <div className={`w-2 h-2 rounded-full ${isTracking ? 'bg-green-300 animate-pulse' : 'bg-red-300'}`}></div>
            <span>{isTracking ? '📍 Tracking Active (Every 5 min)' : '📍 Tracking Stopped'}</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isTracking) {
                stopTracking();
              } else {
                startTracking();
              }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="px-2 py-0.5 bg-white text-green-600 rounded text-xs font-semibold hover:bg-green-50 transition-colors cursor-pointer"
          >
            {isTracking ? 'Stop' : 'Start'}
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsTrackerMinimized(true);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="ml-1 text-white opacity-80 hover:opacity-100 p-1 cursor-pointer"
            title="Minimize"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

// Cache lazy components to prevent re-creating lazy instances on every render cycle, which causes screen blinking
const pageComponentCache: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {};
const getLazyComponent = (path: string) => {
  if (!pageComponentCache[path]) {
    pageComponentCache[path] = lazy(() => import(`./Pages/${path}`));
  }
  return pageComponentCache[path];
};

function App() {
  const user: User = useSelector((state: RootState) => state.user);
  const [CallingUserData, setCallingUserData] = useState<CallingUserData>({});
  const [CallingUserName, setCallingUserName] = useState<{ name?: string }>({});
  const ModalSettings = useSelector((state: RootState) => state.call_modal);
  const ROOMIDS = useSelector((state: RootState) => state.chats?.roomids);
  const CHATS = useSelector((state: RootState) => state.chats?.data);
  const [VideoModal, setVideoModal] = useState<boolean>(false);
  const [AudioModal, setAudioModal] = useState<boolean>(false);
  const CurrentRoomId = useRef<string | null>(null);
  const dispatch:any = useDispatch();

  // Initialize driver location tracking
  const { isTracking, sendLocationNow, stopTracking, startTracking } = useDriverLocation();

  const navContext: any = useContext(UNSAFE_NavigationContext);
  const encryptedNavigator = useMemo(() => {
    if (!navContext?.navigator) return navContext?.navigator;
    return {
      ...navContext.navigator,
      push(to: any, state: any, opts: any) {
        return navContext.navigator.push(encryptPath(to), state, opts);
      },
      replace(to: any, state: any, opts: any) {
        return navContext.navigator.replace(encryptPath(to), state, opts);
      },
      createHref(to: any) {
        return navContext.navigator.createHref(encryptPath(to));
      },
      encodeLocation(to: any) {
        return navContext.navigator.encodeLocation
          ? navContext.navigator.encodeLocation(encryptPath(to))
          : encryptPath(to);
      },
    };
  }, [navContext]);

  const navigationContextValue = useMemo(() => {
    return {
      ...navContext,
      navigator: encryptedNavigator,
    };
  }, [navContext, encryptedNavigator]);

  useEffect(() => {
    dispatch(chats_saver()(user));
  }, []);

  useEffect(() => {
    const userId = user?.id || user?._id;
    if (user?.loggedIn && userId) {
      socketModel.emit("user-online", { user_id: userId });

      const handleConnect = () => {
        socketModel.emit("user-online", { user_id: userId });
      };

      const handleWindowOnline = () => {
        socketModel.emit("user-online", { user_id: userId });
      };

      socketModel.on("connect", handleConnect);
      window.addEventListener("online", handleWindowOnline);

      return () => {
        socketModel.off("connect", handleConnect);
        window.removeEventListener("online", handleWindowOnline);
      };
    }
  }, [user?.loggedIn, user?.id, user?._id]);

  const routes: RouteConfig[] = [
    { url: "/", path: "Home" },
    { url: "/", element: <Navigate to="/home" /> },
    { url: "/aboutus", path: "AboutUs" },
    { url: "/kpi-boards", path: "KpiBoards" },
    { url: "/delivery-stats", path: "Analytics" },
    { url: "/deleteaccount", path: "DeleteAccount" },
    { url: "/privacypolicy", path: "PrivacyPolicy" },
    { url: "/termcondtitons", path: "TermAndCondition" },
    { url: "/contactus", path: "ContactUs" },
    { url: "/login", path: "Login" },
    { url: "/signup", path: "Signup" },
    { url: "/signup-step-1", path: "Signup/AddressStep" },
    { url: "/signup-step-2", path: "Signup/Step2" },
    { url: "/signup-driver-detail", path: "Signup/NewDocument" },
    { url: "/signup-step-3", path: "Signup/Step3" },
    { url: "/chat", path: "Chat" },
    { url: "/dashboard", path: "Dashboard" },
    { url: "/profile", path: "Profile" },
    { url: "/forgotpassword", path: "Forgotpassword" },
    { url: "/otp-verify", path: "Forgotpassword/OTPverify/html" },
    { url: "/dazhboard", element: <Navigate to="/plan" /> },
    { url: "/plan", path: "Plan" },
    { url: "/activeplan", path: "Plan/ActivePlan" },
    { url: "/live-tracking", path: "LiveTracking" },
    { url: "/drivers", path: "Driver" },
    { url: "/recruitment", path: "Recruitment" },
    { url: "/drivers/edit/:id", path: "Driver/AddEdit" },
    { url: "/drivers/add", path: "Driver/AddEdit" },
    { url: "/drivers/detail/:id", path: "Driver/View" },
    { url: "/trucks", path: "Truck" },
    { url: "/trucks/add", path: "Truck/AddTruck" },
    { url: "/trucks/edit/:id", path: "Truck/AddTruck" },
    { url: "/trucks/detail/:id", path: "Truck/View" },
    { url: "/carriers", path: "Carrier" },
    { url: "/carriers/detail/:id", path: "Carrier/View" },
    { url: "/approvedcarriers", path: "Carrier/ApprovedCarrier" },
    { url: "/rejectedcarrier", path: "Carrier/RejectedCarrier" },
    { url: "/pendingcarrier", path: "Carrier/PendingCarrier" },
    { url: "/carriers/edit/:id", path: "Carrier/EditCarrier" },
    { url: "/transaction", path: "Transaction" },
    { url: "/transaction/detail/:id", path: "Transaction/Detail" },
    { url: "/carrierstaff", path: "CarrierStaff" },
    { url: "/carrierstaff/edit/:id", path: "CarrierStaff/AddEdit" },
    { url: "/carrierstaff/add", path: "CarrierStaff/AddEdit" },
    { url: "/carrierstaff/detail/:id", path: "CarrierStaff/View" },
    { url: "/successpage", path: "SuccessPage/html" },
    { url: "/cancelpage", path: "CancelPage/html" },
    { url: "/loads", path: "Loads" },
    { url: "/loads/edit/:id", path: "Loads/AddEdit" },
    { url: "/loads/add", path: "Loads/AddEdit" },
    { url: "/loads/detail/:id", path: "Loads/View" },
    { url: "/pickeduploads", path: "Loads/PickedupLoads" },
    { url: "/intransitloads", path: "Loads/IntransitLoads" },
    { url: "/deliveredloads", path: "Loads/DeliveredLoads" },
    { url: "/pendingpickuploads", path: "Loads/PendingPickupLoads" },
    { url: "/task", path: "TaskManagement" },
    { url: "/task/detail/:id", path: "TaskManagement/View" },
    { url: "/pendingpickuptask", path: "TaskManagement/PendingPickupTask" },
    { url: "/pickeduptask", path: "TaskManagement/PickedupTask" },
    { url: "/intransittask", path: "TaskManagement/IntransitTask" },
    { url: "/deliveredtask", path: "TaskManagement/DeliveredTask" },
    { url: "/tasks/edit/:id", path: "Tasks/AddEditTask" },
    { url: "/task/edit/:id", path: "Tasks/AddEditTask" },
    { url: "/tasks/add", path: "Tasks/AddEditTask" },
    { url: "/tasks", path: "Tasks" },
    { url: "/tasks/detail/:id", path: "Tasks/View" },
    { url: "/team/edit/:id", path: "Team/AddEditTask" },
    { url: "/team/add", path: "Team/AddEditTask" },
    { url: "/team", path: "Team" },
    { url: "/team/detail/:id", path: "Team/View" },
    { url: "/department/edit/:id", path: "Department/AddEdit" },
    { url: "/department/add", path: "Department/AddEdit" },
    { url: "/department", path: "Department" },
    { url: "/department/detail/:id", path: "Department/View" },
    { url: "/shipmentnotes", path: "ShipmentNotes" },
    { url: "/shipmentnotes/detail/:id", path: "ShipmentNotes/View" },
    { url: "/notification", path: "Notification" },
    { url: "/notification/detail/:id", path: "Notification/View" },
    { url: "/driver-dashboard", path: "Dashboard/DriverDashboard" },
    { url: "/subscription-receipt", path: "SubscriptionReceipt" },
    { url: "/submit-receipt", path: "SubscriptionReceipt" }
  ];

  useEffect(() => {
    setVideoModal(Boolean(ModalSettings?.video_modal));
  }, [ModalSettings?.video_modal]);

  useEffect(() => {
    setAudioModal(Boolean(ModalSettings?.audio_modal));
  }, [ModalSettings?.audio_modal]);

  const roomIdsRef = useRef(ROOMIDS);
  const chatsRef = useRef(CHATS);
  const userRef = useRef(user);

  useEffect(() => {
    roomIdsRef.current = ROOMIDS;
    chatsRef.current = CHATS;
    userRef.current = user;
  }, [ROOMIDS, CHATS, user]);

  const RoomIdDetector = (data: any, type: 'Video' | 'Audio', resp: boolean): void => {
    const currentUserId = userRef.current?.id || userRef.current?._id;
    const isUserLoggedIn = Boolean(currentUserId || userRef.current?.token || userRef.current?.access_token);
    
    if (!isUserLoggedIn) {
      return;
    }

    let roomId = data?.data?.room_id || data?.room_id || data?.data?.channel || data?.channel || data?.data?.room_idd || data?.room_idd;
    let callerId = data?.data?.user_id || data?.user_id || data?.data?.sender || data?.sender || data?.data?.sender_id || data?.sender_id || data?.data?.from || data?.from || data?.data?.caller_id || data?.caller_id;
    
    if (!resp) {
      if (type === 'Video') {
        setVideoModal(false);
        dispatch(video_modal(false));
      } else {
        setAudioModal(false);
        dispatch(audio_modal(false));
      }
      CurrentRoomId.current = null;
      return;
    }

    if (!roomId) return;

    // Verify user belongs to this chat room/group before ringing or showing modal
    if (chatsRef.current && chatsRef.current.length > 0) {
      const roomMatch = chatsRef.current.find(
        (_i: any) => String(_i?.room_id) === String(roomId) || String(_i?._id) === String(roomId)
      );
      if (roomMatch) {
        const roomMembers = roomMatch?.room_members || [];
        if (Array.isArray(roomMembers) && roomMembers.length > 0) {
          const isMember = roomMembers.some(
            (m: any) => String(m?.user_id || m?._id || m?.id) === String(currentUserId)
          );
          if (!isMember) {
            return; // User is not a member of this group
          }
        }
      }
    }

    const isSelfCall = Boolean(
      currentUserId &&
      callerId &&
      String(currentUserId) === String(callerId)
    );

    if (isSelfCall) {
      return; // Do NOT show incoming call modal on caller's own side
    }

    DetailsFinder(callerId, roomId);
    socketModel.emit('join-room', { room_id: roomId, user_id: currentUserId });

    CurrentRoomId.current = String(roomId);

    if (type === 'Video') {
      setVideoModal(true);
      dispatch(video_modal(true));
    } else {
      setAudioModal(true);
      dispatch(audio_modal(true));
    }
  };

  const DetailsFinder = (id: string, room_Id: string): void => {
    const targetRoomStr = String(room_Id || '');
    const RoomDetails = chatsRef.current?.find(
      (_i: any) => String(_i?.room_id || _i?._id || _i?.id || '') === targetRoomStr
    );
    const AllRoomMembers = RoomDetails?.room_members || [];
    const userData = AllRoomMembers?.find(
      (_data: any) => String(_data?.user_id || _data?._id || _data?.id) === String(id)
    );

    const isGroup = Boolean(
      RoomDetails?.isGroupChat ||
      RoomDetails?.groupType ||
      RoomDetails?.group_type ||
      RoomDetails?.type === 'group' ||
      (Array.isArray(AllRoomMembers) && AllRoomMembers.length > 2)
    );

    const callerPersonName = userData?.user_name || userData?.fullName || userData?.name;
    const groupName = RoomDetails?.room_name || "Group Call";

    if (isGroup) {
      const displayName = callerPersonName
        ? `${callerPersonName} calling in ${groupName}`
        : groupName;
      setCallingUserName({ name: groupName });
      setCallingUserData({
        ...RoomDetails,
        ...userData,
        room_id: room_Id,
        room_name: groupName,
        isGroupChat: true,
        name: displayName,
        user_image: RoomDetails?.room_image || userData?.user_image
      });
    } else {
      const callerName = callerPersonName || RoomDetails?.room_name || "Caller";
      setCallingUserName({ name: callerName });
      setCallingUserData({
        ...RoomDetails,
        ...userData,
        room_id: room_Id,
        isGroupChat: false,
        name: `${callerName} is calling....`,
        user_image: userData?.user_image || userData?.image || RoomDetails?.room_image
      });
    }
  };

  useEffect(() => {
    const handleRecieveVoiceCall = (data: any) => RoomIdDetector(data, 'Audio', true);
    const handleRecieveVideoCall = (data: any) => RoomIdDetector(data, 'Video', true);
    const handleStopVideoCall = (data: any) => {
      localStorage.removeItem("BackToVedioCall");
      RoomIdDetector(data, 'Video', false);
    };
    const handleStopVoiceCall = (data: any) => {
      localStorage.removeItem("BackToVoiceCall");
      RoomIdDetector(data, 'Audio', false);
    };

    socketModel.on("recieve-voice-call", handleRecieveVoiceCall);
    socketModel.on("recieve-video-call", handleRecieveVideoCall);
    socketModel.on("stop-video-call", handleStopVideoCall);
    socketModel.on("stop-voice-call", handleStopVoiceCall);

    return () => {
      socketModel.off("recieve-voice-call", handleRecieveVoiceCall);
      socketModel.off("recieve-video-call", handleRecieveVideoCall);
      socketModel.off("stop-video-call", handleStopVideoCall);
      socketModel.off("stop-voice-call", handleStopVoiceCall);
    };
  }, []);

  useEffect(() => {
    dispatch(video_modal(false));
    dispatch(audio_modal(false));
  }, [dispatch]);

  // Optional: Add keyboard shortcut for manual location update (Ctrl+Shift+L)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent): void => {
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        if (user?.role === 'driver') {
          sendLocationNow();
          console.log('Manual location update triggered via keyboard');
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [user?.role, sendLocationNow]);

  return (
    <NavigationContextProvider value={navigationContextValue}>
      <SidebarProvider>
        <Suspense
          fallback={
            <div className="loaderDiv">
              <div>
                <img
                  src="/assets/img/loader.gif"
                  alt="logo"
                  className="loaderlogo"
                />
              </div>
            </div>
          }
        >
          <Routes>
            {routes.map((itm, index) => {
              if (!itm.path) {
                return (
                  <Route
                    key={itm.url || index}
                    path={itm.url}
                    element={itm.element}
                  />
                );
              }
              const Element = getLazyComponent(itm.path);
              return (
                <Route
                  key={`${itm.url}-${itm.path}-${index}`}
                  path={itm.url}
                  element={
                    <PageRoute CallingUserData={CallingUserData} CallingUserName={CallingUserName}>
                      <Element />
                    </PageRoute>
                  }
                />
              );
            })}
          </Routes>
        </Suspense>
      </SidebarProvider>

      <div id="loader" className="loaderDiv d-none">
        <div>
          <img src="/assets/img/loader.gif" alt="logo" className="loaderlogo" />
        </div>
      </div>
      <ToastContainer position="top-right" />

      {/* Driver Location Tracking Status Indicator (Movable Anywhere) */}
      {user?.role === 'driver' && (
        <DriverTrackerWidget
          isTracking={isTracking}
          sendLocationNow={sendLocationNow}
          startTracking={startTracking}
          stopTracking={stopTracking}
        />
      )}

      {/* Headless UI MODAL FOR VIDEO AND AUDIO CALL */}
      <AudioCallRingModal isOpen={AudioModal} setIsOpen={setAudioModal} roomId={CurrentRoomId.current} CallingUserData={CallingUserData} />
      <VideoCallRingModal isOpen={VideoModal} setIsOpen={setVideoModal} roomId={CurrentRoomId.current} CallingUserData={CallingUserData} />
    </NavigationContextProvider>
  );
}

export default App;